import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LandingInfoSectionProps {
  content: any;
  theme: any;
  businessInfo: any;
  location: any;
  onTrackPhoneClick?: (phone: string) => void;
  onTrackEmailClick?: (email: string) => void;
  onTrackDirectionsClick?: (address: string) => void;
}

const LandingInfoSection = ({
  content,
  theme,
  businessInfo,
  location,
  onTrackPhoneClick,
  onTrackEmailClick,
  onTrackDirectionsClick
}: LandingInfoSectionProps) => {
  // Format business hours from GMB data
  const formatBusinessHours = (hours: any) => {
    if (!hours) return null;

    const dayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    const formattedHours: { [key: string]: string } = {};

    // Helper function to format time from hours/minutes object
    const formatTimeFromObject = (timeObj: any) => {
      if (!timeObj || typeof timeObj !== 'object') return '';
      const hours = timeObj.hours || 0;
      const minutes = timeObj.minutes || 0;

      // Handle 24:00 format (end of day)
      if (hours === 24) {
        return '12:00 AM';
      }

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Handle GMB API format with flat periods array containing openDay/closeDay/openTime/closeTime
    if (hours.periods && Array.isArray(hours.periods) && hours.periods.length > 0) {
      const firstPeriod = hours.periods[0];

      // Check if this is the GMB API format with openDay/closeDay and time objects
      if (firstPeriod.openDay && firstPeriod.openTime && typeof firstPeriod.openTime === 'object') {
        hours.periods.forEach((period: any) => {
          const day = period.openDay;
          if (!day) return;

          const openTime = formatTimeFromObject(period.openTime);
          const closeTime = formatTimeFromObject(period.closeTime);

          if (openTime && closeTime) {
            if (formattedHours[day]) {
              // Multiple periods for the same day
              formattedHours[day] += `, ${openTime} - ${closeTime}`;
            } else {
              formattedHours[day] = `${openTime} - ${closeTime}`;
            }
          }
        });

        // Format day names for display
        const displayHours: { [key: string]: string } = {};
        dayOrder.forEach(day => {
          if (formattedHours[day]) {
            const displayDay = day.charAt(0) + day.slice(1).toLowerCase();
            displayHours[displayDay] = formattedHours[day];
          }
        });

        return Object.keys(displayHours).length > 0 ? displayHours : null;
      }

      // Handle old format with numeric day indices
      if (firstPeriod.open?.day !== undefined) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        hours.periods.forEach((period: any) => {
          const dayIndex = period.open?.day || 0;
          const dayName = dayNames[dayIndex];

          if (period.open && period.close) {
            const openTime = period.open.time ?
              `${period.open.time.substring(0, 2)}:${period.open.time.substring(2)}` : '';
            const closeTime = period.close.time ?
              `${period.close.time.substring(0, 2)}:${period.close.time.substring(2)}` : '';

            if (openTime && closeTime) {
              formattedHours[dayName] = `${openTime} - ${closeTime}`;
            }
          } else if (period.open && !period.close) {
            formattedHours[dayName] = 'Open 24 hours';
          }
        });

        return Object.keys(formattedHours).length > 0 ? formattedHours : null;
      }
    }

    // Handle format where each day is an object with periods array
    if (typeof hours === 'object' && !Array.isArray(hours)) {
      dayOrder.forEach(day => {
        if (hours[day]) {
          const dayData = hours[day];

          if (!dayData.isOpen) {
            formattedHours[day] = 'Closed';
          } else if (dayData.is24) {
            formattedHours[day] = 'Open 24 hours';
          } else if (dayData.periods && dayData.periods.length > 0) {
            // Format multiple periods for the same day
            const periods = dayData.periods.map((period: any) => {
              // Check if periods have time objects
              if (period.openTime && period.closeTime && typeof period.openTime === 'object') {
                const openTime = formatTimeFromObject(period.openTime);
                const closeTime = formatTimeFromObject(period.closeTime);
                return `${openTime} - ${closeTime}`;
              }
              // Fallback to string format
              return `${period.open} - ${period.close}`;
            }).join(', ');
            formattedHours[day] = periods;
          }
        }
      });

      // Format day names for display
      const displayHours: { [key: string]: string } = {};
      Object.entries(formattedHours).forEach(([day, hours]) => {
        const displayDay = day.charAt(0) + day.slice(1).toLowerCase();
        displayHours[displayDay] = hours;
      });

      return Object.keys(displayHours).length > 0 ? displayHours : null;
    }

    return null;
  };

  const businessHours = formatBusinessHours(businessInfo?.hours);

  // Create Google Maps embed URL
  const createMapEmbedUrl = () => {
    // If we have a maps_url with CID, use it for embedding
    if (location?.maps_url) {
      // Extract CID from maps_url (format: https://maps.google.com/maps?cid=12278565555337919252)
      const cidMatch = location.maps_url.match(/cid=(\d+)/);
      if (cidMatch && cidMatch[1]) {
        const cid = cidMatch[1];
        // Use the CID to create an embed URL
        return `https://maps.google.com/maps?cid=${cid}&output=embed`;
      }
    }

    // Fallback to coordinates-based embedding if available
    if (location?.latitude && location?.longitude) {
      const query = encodeURIComponent(`${location.name}, ${location.address || businessInfo?.address}`);

      // Try with API key first if available
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (apiKey) {
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}&center=${location.latitude},${location.longitude}&zoom=15`;
      } else {
        // Basic embed without API key
        return `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`;
      }
    }

    return null;
  };

  const mapEmbedUrl = createMapEmbedUrl();

  return (
    <section id="contact-info" className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading mb-4">
            {content?.heading || "Let's Connect"}
          </h2>
          <div className="section-divider" />
          <p className="section-description mt-6">
            {content?.description || "We'd love to hear from you. Drop by, give us a call, or send a message - we're here to help."}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Google Map */}
          <div>
            {mapEmbedUrl ? (
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Business Location"
                />
              </div>
            ) : (
              <div className="w-full h-96 rounded-lg bg-gray-200 flex items-center justify-center">
                <p className="text-gray-500">Map not available</p>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Phone */}
            {businessInfo?.phone && (
              <div className="flex items-start space-x-4">
                <div className="icon-container">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Phone</h3>
                  <a
                    href={`tel:${businessInfo.phone}`}
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                    onClick={() => onTrackPhoneClick?.(businessInfo.phone)}
                  >
                    {businessInfo.phone}
                  </a>
                </div>
              </div>
            )}

            {/* Email - only show if available */}
            {businessInfo?.email && (
              <div className="flex items-start space-x-4">
                <div className="icon-container">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Email</h3>
                  <a
                    href={`mailto:${businessInfo.email}`}
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                    onClick={() => onTrackEmailClick?.(businessInfo.email)}
                  >
                    {businessInfo.email}
                  </a>
                </div>
              </div>
            )}

            {/* Address */}
            {(location?.address || businessInfo?.formatted_address) && (
              <div className="flex items-start space-x-4">
                <div className="icon-container">
                  <MapPin className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">Address</h3>
                  <p className="text-gray-600 mb-3">
                    {location?.address || businessInfo?.formatted_address}
                  </p>
                  <Button
                    size="sm"
                    style={{
                      backgroundColor: "var(--primary)",
                      color: "var(--primary-foreground)",
                    }}
                    onClick={() => {
                      const address = location?.address || businessInfo?.formatted_address;
                      // Track analytics
                      if (onTrackDirectionsClick) {
                        onTrackDirectionsClick(address);
                      }
                      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || '')}`;
                      window.open(mapsUrl, '_blank');
                    }}
                  >
                    Get Directions
                  </Button>
                </div>
              </div>
            )}

            {/* Business Hours */}
            {businessHours && Object.keys(businessHours).length > 0 && (
              <div className="flex items-start space-x-4">
                <div className="icon-container">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Business Hours</h3>
                  <div className="text-gray-600 space-y-1">
                    {Object.entries(businessHours).map(([day, hours]) => (
                      <div key={day} className="flex justify-between min-w-0">
                        <span className="font-medium mr-4">{day}:</span>
                        <span className="text-right">{hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingInfoSection;